import { useEffect, useMemo, useRef } from "react";

import { useIntl } from "react-intl";

import { Editions, ProductKeys } from "@atlassiansox/asc-stargate";

import { useCrossFlowFrontendTouchpoint } from "@/components/cross-flow/touchpoint/use-touchpoint/index";
import { useFireEvent } from "@/components/cross-flow/touchpoint/utils";
import { useLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";
import { LogoV3 } from "@/libraries/wpl/foundations/src/logo-v3";
import { LogoV2 } from "@/libraries/wpl/foundations/src/logov2";
import { DropdownContent } from "@/patterns/global-nav/src/dropdown-content";
import { GlobalNavLinkContentProps } from "@/patterns/global-nav/src/nav-links-container";
import { ProductPageSubNav } from "@/patterns/global-nav/src/nav-product-page";
import { experiments } from "@/utils/experiment/manifest";
import { useExperiment } from "@/utils/experiment/use-experiment";
import { checkFeatureFlagGate } from "@/utils/feature-flags/check-feature-flag";
import { featureFlags } from "@/utils/feature-flags/feature-flag-manifest";

import { useAnalyticsClient } from "../../../../../../../analytics/wac-analytics/analytics-client";
import { GlobalNavProvider } from "../../../../../../../patterns/global-nav/src/nav-links-container";

const JiraServiceManagementLogo = () => {
    const gwp7518NavIconUpdates = checkFeatureFlagGate(
        featureFlags.gwp7518NavIconUpdates
    );

    return gwp7518NavIconUpdates ? (
        <LogoV3 brand="jiraservicemanagement" type="logo" appearance="brand" />
    ) : (
        <LogoV2 brand="jiraservicemanagement" type="logo" appearance="brand" />
    );
};

export type JiraServiceManagementSoftwareNavV2Props = {
    productNavCtaCTAClickFunction?: (
        event: React.MouseEvent<HTMLButtonElement>
    ) => void;
    isUseOpenCrossFlow?: boolean;
};

const JiraServiceManagementSoftwareNavV2 = ({
    productNavCtaCTAClickFunction,
    isUseOpenCrossFlow = false
}: JiraServiceManagementSoftwareNavV2Props) => {
    const gwp7518NavIconUpdates = checkFeatureFlagGate(
        featureFlags.gwp7518NavIconUpdates
    );
    const intl = useIntl();
    const featuresHref = useLocalizedHref(
        "/software/jira/service-management/features"
    );
    const pricingHref = useLocalizedHref(
        "/software/jira/service-management/pricing"
    );
    const itSupportHref = useLocalizedHref(
        "/software/jira/service-management/itsm-software"
    );
    const itOperationsHref = useLocalizedHref(
        "/software/jira/service-management/it-operations"
    );
    const enterpriseServiceManagementHref = useLocalizedHref(
        "/software/jira/service-management/features/enterprise-service-management"
    );
    const customerServiceHref = useLocalizedHref(
        "/software/jira/service-management/customer-service-software"
    );
    const hrHref = useLocalizedHref("/software/jira/service-management/hr");
    const smallTeamsHref = useLocalizedHref(
        "/software/jira/service-management/help-desk-software-small-business-software"
    );
    const premiumHref = useLocalizedHref(
        "/software/jira/service-management/premium"
    );
    const enterpriseHref = useLocalizedHref(
        "/software/jira/service-management/enterprise"
    );
    const technologyTelecomHref = useLocalizedHref(
        "/software/jira/service-management/technology-telecommunications"
    );
    const financialServicesHref = useLocalizedHref(
        "/software/jira/service-management/financial-services"
    );
    const retailHref = useLocalizedHref(
        "/software/jira/service-management/retail-ecommerce"
    );
    const manufacturingHref = useLocalizedHref(
        "/software/jira/service-management/manufacturing"
    );
    const itsmHref = useLocalizedHref("/itsm");
    const productGuideHref = useLocalizedHref(
        "/software/jira/service-management/product-guide"
    );
    const resourcesHref = useLocalizedHref(
        "/software/jira/service-management/resources"
    );

    const solutionsDropdownStyles = useMemo(() => {
        return {
            width: "fit-content !important",
            margin: 0,
            transform: "translateX(-40%)"
        };
    }, []);

    const templatesUrl = useLocalizedHref(
        "/software/jira/templates/service-management"
    );

    const { fireUIEvent } = useFireEvent();

    const viewedUIEvent = useRef(false);
    const analyticsClient = useAnalyticsClient();

    useEffect(() => {
        if (!viewedUIEvent.current && analyticsClient) {
            viewedUIEvent.current = true;
            fireUIEvent({
                action: "viewed",
                actionSubject: "jiraServiceManagementSoftwareNavV2"
            });
        }
    }, [analyticsClient, fireUIEvent]);

    const links: GlobalNavLinkContentProps[] = useMemo(
        () => [
            {
                globalLinksKey: "features",
                linkText: intl.formatMessage({
                    id: "software-jira-service-management.navbar.features",
                    defaultMessage: "Features"
                }),
                hasSubnav: false,
                productKey: "jira-servicedesk.ondemand",
                href: featuresHref
            },
            {
                globalLinksKey: "pricing",
                linkText: intl.formatMessage({
                    id: "software-jira-service-management.navbar.pricing",
                    defaultMessage: "Pricing"
                }),
                hasSubnav: false,
                productKey: "jira-servicedesk.ondemand",
                href: pricingHref
            },
            {
                globalLinksKey: "solutions",
                linkText: intl.formatMessage({
                    id: "software-jira-service-management.navbar.solutions",
                    defaultMessage: "Solutions"
                }),
                hasSubnav: true,
                dropdownType: "solutions",
                productKey: "jira-servicedesk.ondemand",
                containerPosition: "relative",
                containerStyles: solutionsDropdownStyles,
                content: (
                    <DropdownContent
                        contentSections={[
                            {
                                header: intl.formatMessage({
                                    id: "software-jira-service-management.navbar.solutions.useCases.header",
                                    defaultMessage: "By use case"
                                }),
                                links: [
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.solutions.useCases.itSupport",
                                            defaultMessage: "IT Support"
                                        }),
                                        href: itSupportHref
                                    },
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.solutions.useCases.itOperations",
                                            defaultMessage: "IT Operations"
                                        }),
                                        href: itOperationsHref
                                    },
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.solutions.useCases.businessTeams",
                                            defaultMessage: "Business Teams"
                                        }),
                                        href: enterpriseServiceManagementHref
                                    },
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.solutions.useCases.customerService",
                                            defaultMessage: "Customer Service"
                                        }),
                                        href: customerServiceHref
                                    },
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.solutions.useCases.humanResources",
                                            defaultMessage: "Human Resources"
                                        }),
                                        href: hrHref
                                    }
                                ]
                            },
                            {
                                header: intl.formatMessage({
                                    id: "software-jira-service-management.navbar.solutions.capability.header",
                                    defaultMessage: "By capability"
                                }),
                                links: [
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.solutions.capability.smallTeams",
                                            defaultMessage: "Small Teams"
                                        }),
                                        href: smallTeamsHref
                                    },
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.solutions.capability.premium",
                                            defaultMessage: "Premium"
                                        }),
                                        href: premiumHref
                                    },
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.solutions.capability.enterprise",
                                            defaultMessage: "Enterprise"
                                        }),
                                        href: enterpriseHref
                                    }
                                ]
                            },
                            {
                                header: intl.formatMessage({
                                    id: "software-jira-service-management.navbar.solutions.industry.customerService",
                                    defaultMessage: "By industry"
                                }),
                                links: [
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.solutions.industry.technologyTelecom",
                                            defaultMessage:
                                                "Technology & Telecom"
                                        }),
                                        href: technologyTelecomHref
                                    },
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.solutions.industry.financialServices",
                                            defaultMessage: "Financial Services"
                                        }),
                                        href: financialServicesHref
                                    },
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.solutions.industry.retail",
                                            defaultMessage: "Retail"
                                        }),
                                        href: retailHref
                                    },
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.solutions.useCases.manufacturing",
                                            defaultMessage: "Manufacturing"
                                        }),
                                        href: manufacturingHref
                                    }
                                ]
                            }
                        ]}
                    />
                )
            },
            {
                globalLinksKey: "resources",
                linkText: intl.formatMessage({
                    id: "software-jira-service-management.navbar.resources",
                    defaultMessage: "Resources"
                }),
                hasSubnav: true,
                dropdownType: "resources",
                productKey: "jira-servicedesk.ondemand",
                containerPosition: "relative",
                content: (
                    <DropdownContent
                        contentSections={[
                            {
                                header: intl.formatMessage({
                                    id: "software-jira-service-management.navbar.resources.type.header",
                                    defaultMessage: "By type"
                                }),
                                links: [
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.resources.type.itsm",
                                            defaultMessage: "ITSM"
                                        }),
                                        href: itsmHref
                                    },
                                    {
                                        text: gwp7518NavIconUpdates
                                            ? intl.formatMessage({
                                                  id: "software-jira-service-management.navbar.resources.type.productGuide-gwp-7518",
                                                  defaultMessage: "Guide"
                                              })
                                            : intl.formatMessage({
                                                  id: "software-jira-service-management.navbar.resources.type.productGuide",
                                                  defaultMessage:
                                                      "Product Guide"
                                              }),
                                        href: productGuideHref
                                    },
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.templates",
                                            defaultMessage: "Templates"
                                        }),
                                        href: templatesUrl
                                    },
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.resources.type.additionalResources",
                                            defaultMessage:
                                                "Additional Resources"
                                        }),
                                        href: resourcesHref
                                    }
                                ]
                            }
                        ]}
                    />
                )
            }
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            customerServiceHref,
            solutionsDropdownStyles,
            enterpriseHref,
            enterpriseServiceManagementHref,
            featuresHref,
            financialServicesHref,
            hrHref,
            intl,
            itOperationsHref,
            itSupportHref,
            itsmHref,
            manufacturingHref,
            premiumHref,
            pricingHref,
            productGuideHref,
            resourcesHref,
            retailHref,
            smallTeamsHref,
            technologyTelecomHref,
            templatesUrl
        ]
    );

    const signupLink = useLocalizedHref(
        "/software/jira/service-management/free"
    );

    const { openCrossFlow } = useCrossFlowFrontendTouchpoint({
        productKey: ProductKeys.JIRA_SERVICE_DESK,
        edition: Editions.FREE,
        redirectUrl: signupLink
    });

    const productNavOnClick = useMemo(
        () =>
            productNavCtaCTAClickFunction && isUseOpenCrossFlow
                ? productNavCtaCTAClickFunction
                : openCrossFlow,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [openCrossFlow, productNavCtaCTAClickFunction]
    );

    const globalNavItemKeys = useMemo(() => {
        return links.map((link) => link.globalLinksKey);
    }, [links]);

    const productNavGoToAppCta = useExperiment(
        experiments.productNavGoToAppCta
    );
    const isExperimentEnabled =
        productNavGoToAppCta.type === "enrolled" &&
        productNavGoToAppCta.value === true;
    const ctaText = isExperimentEnabled
        ? intl.formatMessage({
              id: "productNavBar.openJSM",
              defaultMessage: "Open App"
          })
        : intl.formatMessage({
              id: "productNavBar.get-it-free-button",
              defaultMessage: "Get it free"
          });

    return (
        <GlobalNavProvider globalNavItemKeys={globalNavItemKeys}>
            <ProductPageSubNav
                productLogo={<JiraServiceManagementLogo />}
                mobileLogo={<JiraServiceManagementLogo />}
                productLogoUrl={useLocalizedHref(
                    "/software/jira/service-management"
                )}
                productLogoAriaLabel="Jira Service Management"
                productPageNavLinks={links}
                isShowTryNowLink={false}
                mobileLinksSections={[
                    {
                        mobileLinkKey: "mobile-jira-service-management",
                        mobileLinksProps: links
                    }
                ]}
                isIncludeMobileCTA={false}
                mobileCtaText={intl.formatMessage({
                    id: "productNavBar.get-it-free-button",
                    defaultMessage: "Get it free"
                })}
                mobileCtaUrl={signupLink}
                mobileCtaOnClick={(e: React.MouseEvent) => {
                    if (productNavOnClick) {
                        productNavOnClick(
                            e as React.MouseEvent<HTMLButtonElement>
                        );
                    }
                }}
                navigationTrynowCtaText={ctaText}
                navigationTrynowCtaUrl={signupLink}
                navigationTrynowCtaOnClick={(e: React.MouseEvent) => {
                    if (productNavOnClick) {
                        productNavOnClick(
                            e as React.MouseEvent<HTMLButtonElement>
                        );
                    }
                }}
                additionalMenuPageObjectsArray={[
                    {
                        menuPageKey: "solutions",
                        menuPageObjects: links.find(
                            (link) => link.globalLinksKey === "solutions"
                        )?.content as JSX.Element
                    },
                    {
                        menuPageKey: "resources",
                        menuPageObjects: links.find(
                            (link) => link.globalLinksKey === "resources"
                        )?.content as JSX.Element
                    }
                ]}
            />
        </GlobalNavProvider>
    );
};

export default JiraServiceManagementSoftwareNavV2;
